/**
 * PwaInstallPrompt.jsx
 *
 * Contextual "Installer l'app" banner that only appears once the user has
 * expressed interest in the product (3+ page visits) AND the browser
 * confirms the PWA is installable (`beforeinstallprompt` event).
 *
 * Behaviour
 * ---------
 *   • Mobile-only (viewport < 768 px). We never bother desktop users.
 *   • Requires `beforeinstallprompt` from the browser — no fake iOS UX yet
 *     (Safari doesn't fire the event; we surface an iOS-friendly hint below).
 *   • Waits until the user has clocked 3 page-loads on the site. This is
 *     tracked in localStorage under `r237:pageviews` and bumped by an
 *     effect at every location change from the Router.
 *   • Dismissible for 30 days via localStorage `r237:pwa-dismissed-until`.
 *     Accepting the prompt (or the browser auto-installing) hides the
 *     banner permanently.
 *   • Bottom-anchored, above the MobileBottomTabBar (z-40) — matches
 *     `safe-area-inset-bottom` on iPhone.
 *
 * Never renders if:
 *   • display-mode is already `standalone` (already installed)
 *   • the user is currently on a Pro / admin route (avoid pushing PWA
 *     during power-user workflows)
 *   • the user has dismissed within the last 30 days
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Sparkles } from 'lucide-react';
import {
  trackPwaPromptShown,
  trackPwaPromptInstalled,
  trackPwaPromptDismissed,
} from '../utils/analytics';

const PV_KEY = 'r237:pageviews';
const DISMISS_KEY = 'r237:pwa-dismissed-until';
const INSTALLED_KEY = 'r237:pwa-installed';
const PAGEVIEW_THRESHOLD = 3;
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const HIDDEN_ROUTES = ['/admin', '/pro-dashboard', '/a/'];

const readNumber = (key) => {
  try { return parseInt(localStorage.getItem(key) || '0', 10) || 0; } catch { return 0; }
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true // iOS Safari
  );
};

const PwaInstallPrompt = () => {
  const location = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [pageviews, setPageviews] = useState(() => readNumber(PV_KEY));
  // Track when the banner first became visible so we can compute a
  // "time-to-install" metric on the installed event.
  const shownAtRef = useRef(0);
  // Guard against dispatching pwa_prompt_shown multiple times if the effect
  // re-runs on unrelated dep changes (e.g. pageview bump).
  const shownDispatchedRef = useRef(false);

  // Track page navigations — one increment per pathname change.
  useEffect(() => {
    const next = readNumber(PV_KEY) + 1;
    try { localStorage.setItem(PV_KEY, String(next)); } catch { /* quota */ }
    setPageviews(next);
  }, [location.pathname]);

  // Capture the beforeinstallprompt event as soon as it fires — we may not
  // have reached the pageview threshold yet, so we store it for later.
  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(INSTALLED_KEY) === '1') return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* quota */ }
      // Fires when the PWA is installed via ANY route — our button, the
      // browser menu, or the native banner. If our button already reported
      // the install, this is a harmless dedupe on the PostHog side.
      trackPwaPromptInstalled({ source: 'appinstalled_event' });
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Decide visibility whenever the input signals change.
  useEffect(() => {
    if (!deferredPrompt) { setVisible(false); return; }
    if (pageviews < PAGEVIEW_THRESHOLD) { setVisible(false); return; }
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      setVisible(false); return;
    }
    const until = readNumber(DISMISS_KEY);
    if (until && Date.now() < until) { setVisible(false); return; }
    if (HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r))) {
      setVisible(false); return;
    }
    setVisible(true);
    // Fire the shown event ONCE per prompt lifecycle (until the user
    // installs/dismisses or the deferred prompt is cleared).
    if (!shownDispatchedRef.current) {
      shownAtRef.current = Date.now();
      shownDispatchedRef.current = true;
      trackPwaPromptShown({ pageviews, route: location.pathname });
    }
  }, [deferredPrompt, pageviews, location.pathname]);

  const handleDismiss = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + COOLDOWN_MS)); } catch { /* quota */ }
    setVisible(false);
    trackPwaPromptDismissed('close_button', {
      time_visible_ms: shownAtRef.current ? Date.now() - shownAtRef.current : 0,
    });
    shownDispatchedRef.current = false;
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      const timeMs = shownAtRef.current ? Date.now() - shownAtRef.current : 0;
      if (choice?.outcome === 'accepted') {
        try { localStorage.setItem(INSTALLED_KEY, '1'); } catch { /* quota */ }
        trackPwaPromptInstalled({ time_to_install_ms: timeMs });
      } else {
        // User dismissed the native dialog — treat as a soft dismiss so we
        // don't hammer them next page load.
        try { localStorage.setItem(DISMISS_KEY, String(Date.now() + COOLDOWN_MS)); } catch { /* quota */ }
        trackPwaPromptDismissed('native_dialog_dismissed', { time_visible_ms: timeMs });
      }
    } catch {
      // Some browsers throw if prompt() is called twice — silently swallow.
    } finally {
      setDeferredPrompt(null);
      setInstalling(false);
      setVisible(false);
      shownDispatchedRef.current = false;
    }
  }, [deferredPrompt]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-16 z-[45] md:hidden px-3"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      role="dialog"
      aria-label="Installer l'application"
      data-testid="pwa-install-prompt"
    >
      <div className="mx-auto max-w-md rounded-2xl bg-white/95 backdrop-blur-md shadow-lg border border-stone-200 p-3 flex items-center gap-3">
        <div className="shrink-0 h-11 w-11 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-stone-900 leading-tight">
            Installer Realty237
          </p>
          <p className="text-xs text-stone-500 leading-snug">
            Accès instantané, hors-ligne et notifications futures.
          </p>
        </div>
        <button
          type="button"
          onClick={handleInstall}
          disabled={installing}
          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold px-3 min-h-[40px] disabled:opacity-60"
          data-testid="pwa-install-cta"
        >
          <Download className="h-4 w-4" />
          Installer
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Ignorer"
          className="shrink-0 inline-flex h-11 w-11 items-center justify-center rounded-full text-stone-500 hover:text-stone-900 hover:bg-stone-100"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
